import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { TFunction } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import T from 'src/components/content-manager/translator/T';
import DialogTitleWithClose from 'src/components/elements/DialogTitleWithClose';
import { useAccount } from 'src/core/hooks/account';
import { LanguageKeys, RegionKeys, useAppMeta } from 'src/core/provider/AppMeta';
import { LocaleFilterContext } from 'src/core/provider/LocaleFilterContext';
import { fetchCustomerAddresses } from 'src/store/customer/addresses/actions';
import { getCustomerAddresses } from 'src/store/customer/addresses/selectors';

const TextButton = styled(Button)({
    fontSize: 'inherit',
    color: 'inherit',
    fontWeight: 'inherit',
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
    '&.Mui-selected': {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
    },
    '&.Mui-selected [class*="MuiListItemText-primary"]': {
        fontWeight: 700,
    },
    '&.Mui-selected:hover': {
        backgroundColor: theme.palette.primary.main,
    },
}));

const LabelBoth = styled('div')({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
});

const BoldUpper = styled('div')({
    fontWeight: 'bold',
    textTransform: 'uppercase',
});

interface CountryLangSelectorProps {
    hideIcon?: boolean;
    showLabel?: 'region' | 'language' | 'both';
}

const CountryLangSelector: React.FC<CountryLangSelectorProps> = ({ hideIcon, showLabel }) => {
    const appMeta = useAppMeta();
    const { allowedLanguages, allowedRegions, filterByAddresses } = React.useContext(LocaleFilterContext);
    const { t, i18n } = useTranslation();
    const { isLoggedIn } = useAccount();
    const dispatch = useDispatch();
    const customerAddresses = useSelector(getCustomerAddresses);

    // Local State
    const [open, setOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(appMeta.selectedLanguageKey);
    const [selectedRegion, setSelectedRegion] = useState(appMeta.selectedRegionKey);

    useEffect(() => {
        if (!isLoggedIn) return;
        if (customerAddresses.isFetching) return;
        if (customerAddresses.content) return;
        if (customerAddresses.error) return;

        dispatch(fetchCustomerAddresses());
    }, [dispatch, isLoggedIn, customerAddresses.content, customerAddresses.isFetching, customerAddresses.error]);

    const availableRegionKeys = useMemo<RegionKeys[]>(() => {
        const siteRegions = (allowedRegions as RegionKeys[]).filter(r =>
            (appMeta.regionKeys as RegionKeys[]).includes(r)
        );

        if (!isLoggedIn || !filterByAddresses) {
            return siteRegions;
        }

        const regionsFromAddresses = new Set<RegionKeys>();

        const addRegionsFromState = (state: any) => {
            const content = state?.content;
            if (!content) return;

            content.allAddresses?.forEach((addr: { countryIso2: RegionKeys }) => {
                if (addr?.countryIso2) {
                    regionsFromAddresses.add(addr.countryIso2);
                }
            });

            if (content.defaultBillingAddress?.countryIso2) {
                regionsFromAddresses.add(content.defaultBillingAddress.countryIso2 as RegionKeys);
            }

            if (content.defaultDeliveryAddress?.countryIso2) {
                regionsFromAddresses.add(content.defaultDeliveryAddress.countryIso2 as RegionKeys);
            }

            if (content.accountAddress?.countryIso2) {
                regionsFromAddresses.add(content.accountAddress.countryIso2 as RegionKeys);
            }
        };

        addRegionsFromState(customerAddresses);

        return siteRegions.filter(regionKey => regionsFromAddresses.has(regionKey));
    }, [allowedRegions, appMeta.regionKeys, customerAddresses, isLoggedIn]);

    const handleClickOpen = () => {
        setSelectedLanguage(appMeta.selectedLanguageKey);
        setSelectedRegion(appMeta.selectedRegionKey);
        setOpen(true);
    };

    const handleClose = () => {
        // Revert language change back to value from store
        i18n.changeLanguage(appMeta.selectedLocale);
        // Revert local state to values from store
        setSelectedLanguage(appMeta.selectedLanguageKey);
        setSelectedRegion(appMeta.selectedRegionKey);
        setOpen(false);
    };

    const handleSaveClose = () => {
        appMeta.setLocale(selectedLanguage, selectedRegion);
        setOpen(false);
    };

    const handleSelectLanguage = (key: LanguageKeys) => {
        i18n.changeLanguage(`${key}-${selectedRegion}`);
        setSelectedLanguage(key);
    };

    const handleSelectRegion = (key: RegionKeys) => {
        i18n.changeLanguage(`${selectedLanguage}-${key}`);
        setSelectedRegion(key);
    };

    return (
        <>
            <span>
                {showLabel || hideIcon ? (
                    <TextButton
                        variant="text"
                        onClick={handleClickOpen}
                        startIcon={
                            !hideIcon && (
                                <ReactCountryFlag countryCode={appMeta.selectedRegionKey} svg cdnUrl="/flag-icons/" />
                            )
                        }>
                        {showLabel === undefined && (
                            <>
                                <T k="language" />/<T k="region" />
                            </>
                        )}
                        {showLabel === 'language' && (
                            <>
                                <T k="language" />: {t(`languages:${selectedLanguage}`)}
                            </>
                        )}
                        {showLabel === 'region' && (
                            <>
                                <T k="region" />: {t(`countries:${selectedRegion}`)}
                            </>
                        )}
                        {showLabel === 'both' && (
                            <LabelBoth>
                                <div>
                                    <T k="selected_language_region" />
                                </div>
                                <BoldUpper>
                                    {selectedLanguage}/{selectedRegion}
                                </BoldUpper>
                            </LabelBoth>
                        )}
                    </TextButton>
                ) : (
                    <IconButton onClick={handleClickOpen}>
                        <ReactCountryFlag countryCode={appMeta.selectedRegionKey} svg cdnUrl="/flag-icons/" />
                    </IconButton>
                )}
            </span>
            <Dialog
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleClose();
                }}
                aria-labelledby="customized-dialog-title"
                open={open}>
                <DialogTitleWithClose onClose={handleClose} />
                <DialogContent>
                    <Grid container>
                        {/* Language List */}
                        <Grid item style={{ maxWidth: '15em', marginRight: '10em' }}>
                            <Typography variant="h3">{t('language')}</Typography>
                            {renderDualList<LanguageKeys>(
                                'languagelist',
                                (allowedLanguages as LanguageKeys[]).filter(l =>
                                    (appMeta.languageKeys as LanguageKeys[]).includes(l)
                                ),
                                selectedLanguage,
                                t,
                                handleSelectLanguage,
                                'languages'
                            )}
                        </Grid>
                        {/* Region List */}
                        <Grid item style={{ maxWidth: '15em' }}>
                            <Typography variant="h3">{t('region')}</Typography>
                            {renderDualList<RegionKeys>(
                                'regionlist',
                                availableRegionKeys,
                                selectedRegion,
                                t,
                                handleSelectRegion,
                                'countries'
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSaveClose} variant="contained">
                        {t('apply_changes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

function renderDualList<K extends LanguageKeys | RegionKeys>(
    mode: 'languagelist' | 'regionlist',
    items: readonly K[],
    selectedItem: K,
    t: TFunction,
    handleSelect: (item: K) => void,
    namespace: string
): React.ReactNode {
    return (
        <List>
            <Grid container>
                {items.map(item => {
                    const itemName = t(`${namespace}:${item}`);
                    return (
                        <Grid key={item} item xs={12}>
                            <StyledListItem button selected={item === selectedItem} onClick={() => handleSelect(item)}>
                                {mode === 'regionlist' && (
                                    <ListItemIcon>
                                        <ReactCountryFlag
                                            countryCode={item}
                                            aria-label={itemName}
                                            svg
                                            cdnUrl="/flag-icons/"
                                        />
                                    </ListItemIcon>
                                )}
                                <ListItemText primary={itemName} />
                            </StyledListItem>
                        </Grid>
                    );
                })}
            </Grid>
        </List>
    );
}

export default CountryLangSelector;
